import { createMachine } from 'xstate';
import { pure } from 'xstate/lib/actions';

import shared from 'common/bento/shared';
import { AnyMachineEvent } from 'common/bento/types/Abstract';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';

import onboardingLocales from '../../../locales';
import locales from '../locales';
import { getActiveSupposedBeneficiaries } from '../pages/ManageBeneficiariesPage/storage';
import actions, { Action } from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';

export enum State {
  INIT = 'init',
  INTRO = 'intro',
  ENTRY = 'entry',
  EDIT = 'edit',
  BENEFICIARY_LIST = 'beneficiary-list',
  MANAGE_BENEFICIARY = 'manage-beneficiary',
  DELETE_BENEFICIARY = 'delete-beneficiary',
  SUBMIT_BENEFICIARY_FORM = 'submit-beneficiary-form',
  MORAL_BENEFICIARY = 'moral-beneficiary',
  CONFIRM_BENEFICIARIES = 'confirm-beneficiaries',
  SEND_BENEFICIARY_INVITATIONS_WITHOUT_BENEFICIARIES = 'send_beneficiary_invitations_without_beneficiaries',
  SEND_INVITATIONS = 'send-invitations',
  SEND_BENEFICIARY_INVITATIONS = 'send-beneficiary-invitations',
  ABORT_TO_RESET_PROMPT = 'abort-to-reset-prompt',
  ABORT = 'abort',
  DONE = 'done',
}

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'beneficiaries',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: 'showNewOnboardingBeneficiaryFlow',
            target: State.ENTRY,
          },
          {
            target: State.INTRO,
          },
        ],
      },

      [State.ENTRY]: {
        entry: shared.actions.trackView(),
        on: {
          NEXT: State.BENEFICIARY_LIST,

          PREV: [
            {
              cond: Guard.CanSeeResetPrompt,
              target: State.ABORT_TO_RESET_PROMPT,
            },
            {
              target: State.ABORT,
            },
          ],

          SKIP_BENEFICIARY_LIST: {
            actions: shared.actions.track({
              name: 'Onboarding Beneficiary List Skipped',
              properties: {
                from: 'entry',
              },
            }),
            target: State.DONE,
          },
        },
      },

      [State.BENEFICIARY_LIST]: {
        entry: [
          shared.actions.trackView(),
          pure(({ getModuleData }) => {
            const { company } = getModuleData().viewer;

            return shared.actions.track({
              name: 'Onboarding Beneficiary List Displayed',
              properties: {
                newDesign: true,
                totalBeneficiaries: company.legalBeneficiaries.length,
                totalSuggestedBeneficiaries: getActiveSupposedBeneficiaries(
                  company.supposedBeneficiaries,
                ).length,
              },
              usePrefix: false,
            });
          }),
        ],
        on: {
          GO_MANAGE_BENEFICIARY: {
            actions: Action.ViewManageBeneficiaryEvent,
            target: State.MANAGE_BENEFICIARY,
          },
          GO_MORAL_BENEFICIARY: State.MORAL_BENEFICIARY,
          NEXT: [
            {
              cond: 'hasBeneficiaries',
              target: State.CONFIRM_BENEFICIARIES,
            },
            {
              target: State.SEND_BENEFICIARY_INVITATIONS_WITHOUT_BENEFICIARIES,
            },
          ],
          PREV: State.ENTRY,
          SKIP_BENEFICIARY_LIST: {
            actions: shared.actions.track({
              name: 'Onboarding Beneficiary List Skipped',
              properties: {
                from: 'beneficiary-list',
              },
            }),
            target: State.DONE,
          },
        },
      },

      // We still need to call the sendAllCompanyInvitations mutation to trigger the application beneficiary list completed event and set the hasRegisteredAllBeneficiaries prop on the company metadata.
      [State.SEND_BENEFICIARY_INVITATIONS_WITHOUT_BENEFICIARIES]: {
        invoke: {
          onDone: State.DONE,
          onError: {
            actions: shared.actions.error(),
            target: State.BENEFICIARY_LIST,
          },
          src: Service.SendAllCompanyInvitations,
        },
      },

      [State.MANAGE_BENEFICIARY]: {
        on: {
          DELETE_BENEFICIARY: State.DELETE_BENEFICIARY,
          PREV: State.BENEFICIARY_LIST,
          SUBMIT_BENEFICIARY_FORM: State.SUBMIT_BENEFICIARY_FORM,
        },
      },

      [State.SUBMIT_BENEFICIARY_FORM]: {
        invoke: {
          onDone: {
            actions: Action.DisplaySuccessfullySubmittedBeneficiaryFormToast,
            target: State.BENEFICIARY_LIST,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.MANAGE_BENEFICIARY,
          },
          src: Service.SubmitBeneficiaryForm,
        },
      },

      [State.DELETE_BENEFICIARY]: {
        invoke: {
          onDone: {
            actions: Action.DisplaySuccessfullyDeletedBeneficiaryToast,
            target: State.BENEFICIARY_LIST,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.MANAGE_BENEFICIARY,
          },
          src: Service.DeleteBeneficiary,
        },
      },

      [State.INTRO]: {
        entry: shared.actions.trackView(),
        on: {
          NEXT: State.EDIT,

          PREV: [
            {
              cond: Guard.CanSeeResetPrompt,
              target: State.ABORT_TO_RESET_PROMPT,
            },
            // TODO(Carina): There’s no reason to block the PREV here but if
            // the previous module step is complete it will stay stuck in ABORT
            // PREV: State.ABORT,
            {
              actions: shared.actions.toast({
                status: 'error',
                title: onboardingLocales.toast.definitiveStep,
              }),
            },
          ],
        },
      },

      [State.EDIT]: {
        entry: shared.actions.trackView(),
        on: {
          GO_MORAL_BENEFICIARY: State.MORAL_BENEFICIARY,
          NEXT: State.SEND_INVITATIONS,
          PREV: State.INTRO,
        },
      },

      [State.MORAL_BENEFICIARY]: {
        entry: shared.actions.trackView(),
        on: {
          PREV: [
            {
              cond: 'showNewOnboardingBeneficiaryFlow',
              target: State.BENEFICIARY_LIST,
            },
            {
              target: State.EDIT,
            },
          ],
        },
      },

      [State.CONFIRM_BENEFICIARIES]: {
        entry: shared.actions.trackView(),
        on: {
          MARK_BENEFICIARY_LIST_AS_COMPLETE: State.SEND_BENEFICIARY_INVITATIONS,
          PREV: State.BENEFICIARY_LIST,
        },
      },

      [State.SEND_BENEFICIARY_INVITATIONS]: {
        invoke: {
          onDone: {
            actions: [
              shared.actions.toast({
                status: 'success',
                title: locales.invitationSent,
              }),
            ],
            target: State.DONE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.CONFIRM_BENEFICIARIES,
          },
          src: Service.MarkBeneficiaryListAsComplete,
        },
      },

      [State.SEND_INVITATIONS]: {
        invoke: {
          onDone: State.DONE,
          onError: { actions: shared.actions.error(), target: State.EDIT },
          src: Service.SendAllCompanyInvitations,
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT_TO_RESET_PROMPT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },
  {
    actions,
    guards,
    services,
  },
);

export default machine;
